// 🎃 10-07 pumpkins (P7): DRAWING a pumpkin, ported from the 09-28 mockup's render(), with Nina's ruling R22:
// a PAINTED pumpkin under the cuts (carving pumpkin C, public/pumpkin-carve.webp, painted to the code's five
// lobes: carve-ref/overlay-ABC.png shows the outline on it). Canvas only: jsdom has no canvas, so the browser
// harness scripts/verify-pumpkin-carving.js is this file's gate. Until the painting loads (or if it never does),
// the mockup's own code-drawn pumpkin stands in, so a pumpkin always shows.
import { CANVAS, CY, LOBES } from './pumpkinCarve';

export const PAINTING_URL = `${process.env.PUBLIC_URL || ''}/pumpkin-carve.webp`;

let painting = null;
let keyed = null; // { day, lit }: the painting with its own flat background keyed out (keyPainting)
let paintingState = 'idle'; // 'idle' | 'loading' | 'ready' | 'failed'
const waiters = new Set();
/** Load the painting once per page. `onReady` runs when it is ready (or failed: the code pumpkin stays). */
export function loadPainting(onReady) {
  if (paintingState === 'ready' || paintingState === 'failed') { if (onReady) onReady(); return () => {}; }
  if (onReady) waiters.add(onReady);
  if (paintingState === 'idle' && typeof Image !== 'undefined') {
    paintingState = 'loading';
    const img = new Image();
    img.decoding = 'async';
    const done = (ok) => {
      paintingState = ok ? 'ready' : 'failed';
      painting = ok ? img : null;
      keyed = ok ? keyPainting(img) : null;
      Array.from(waiters).forEach((w) => { try { w(); } catch (e) { /* one listener never breaks the rest */ } });
      waiters.clear();
    };
    img.onload = () => done(true);
    img.onerror = () => done(false);
    img.src = PAINTING_URL;
  }
  return () => { if (onReady) waiters.delete(onReady); };
}
export const paintingReady = () => paintingState === 'ready';

function bodyPath(ctx) {
  ctx.beginPath();
  LOBES.forEach((l) => { ctx.moveTo(l.x + l.rx, CY); ctx.ellipse(l.x, CY, l.rx, l.ry, 0, 0, Math.PI * 2); });
}
// the painted stem sits a little wider than the mockup's code stem: a generous box keeps it whole when clipped
const STEM = { x: 262, y: 66, w: 76, h: 92 };
function stemBox(ctx) { ctx.rect(STEM.x, STEM.y, STEM.w, STEM.h); }
const DARK_SKIN = 'rgba(16,6,2,0.74)'; // a carved pumpkin at night is dark skin, bright holes
/**
 * 10-07 (the look): clipping the painting to the code's lobes + a stem BOX showed the painting's own flat purple
 * background (32,22,39) as a box around the stem, on the table and on every porch sprite, and shaved up to 8 units
 * off the painted outline all the way round (a black bar where the box met the middle lobe). Measured on the
 * painting: its background never strays more than 5.7 from that purple, nothing inside the pumpkin comes closer
 * than 30.9, and nothing but background lies beyond 8 units of the code outline. So once, when it loads, every
 * pixel near the corner's colour fades out (≤ 6 clear, ≥ 26 whole, a soft edge between) and the keyed copy draws
 * with no clip. A tainted image (another origin someday) cannot be read: null, and the old clip stands.
 */
function keyPainting(img) {
  try {
    if (typeof document === 'undefined') return null;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return null;
    const day = document.createElement('canvas');
    day.width = w; day.height = h;
    const dx = day.getContext('2d');
    dx.drawImage(img, 0, 0);
    const data = dx.getImageData(0, 0, w, h);
    const p = data.data;
    const [r0, g0, b0] = [p[0], p[1], p[2]]; // the corner is the painting's flat background
    for (let i = 0; i < p.length; i += 4) {
      const dr = p[i] - r0; const dg = p[i + 1] - g0; const db = p[i + 2] - b0;
      const d2 = dr * dr + dg * dg + db * db;
      if (d2 < 676) p[i + 3] = Math.round(p[i + 3] * Math.max(0, (Math.sqrt(d2) - 6) / 20)); // 676 = 26²
    }
    dx.putImageData(data, 0, 0);
    return { day, lit: null };
  } catch (e) {
    return null;
  }
}
/** The night copy (dark skin), made on first use: darkens only the pumpkin's own pixels. */
function keyedLit() {
  if (!keyed.lit) {
    const c = document.createElement('canvas');
    c.width = keyed.day.width; c.height = keyed.day.height;
    const x = c.getContext('2d');
    x.drawImage(keyed.day, 0, 0);
    x.globalCompositeOperation = 'source-atop';
    x.fillStyle = DARK_SKIN;
    x.fillRect(0, 0, c.width, c.height);
    keyed.lit = c;
  }
  return keyed.lit;
}
function codeStem(ctx) {
  ctx.beginPath();
  ctx.moveTo(284, 152); ctx.quadraticCurveTo(276, 112, 298, 90); ctx.lineTo(320, 98);
  ctx.quadraticCurveTo(304, 118, 318, 154); ctx.closePath();
}
/** The mockup's smoothing: quadratic curves through the midpoints. */
function tracePath(ctx, pts, closed) {
  ctx.beginPath();
  if (pts.length < 2) { ctx.arc(pts[0][0], pts[0][1], 0.5, 0, Math.PI * 2); return; }
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i][0] + pts[i + 1][0]) / 2;
    const my = (pts[i][1] + pts[i + 1][1]) / 2;
    ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
  }
  const last = pts[pts.length - 1];
  ctx.lineTo(last[0], last[1]);
  if (closed) ctx.closePath();
}

/** The mockup's code pumpkin (the stand-in while the painting loads). */
function drawCodeBody(ctx, lit) {
  LOBES.forEach((l) => {
    const g = ctx.createRadialGradient(l.x - l.rx * 0.3, CY - l.ry * 0.38, 8, l.x, CY, l.ry * 1.08);
    if (lit) { g.addColorStop(0, '#5a2a10'); g.addColorStop(0.6, '#34170a'); g.addColorStop(1, '#170904'); }
    else { g.addColorStop(0, '#ffbe68'); g.addColorStop(0.55, '#f08a26'); g.addColorStop(1, '#b0501a'); }
    ctx.beginPath(); ctx.ellipse(l.x, CY, l.rx, l.ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = g; ctx.fill();
    ctx.lineWidth = 4; ctx.strokeStyle = lit ? 'rgba(20,8,2,0.7)' : 'rgba(110,44,10,0.55)'; ctx.stroke();
  });
  codeStem(ctx);
  const sg = ctx.createLinearGradient(280, 90, 320, 154);
  if (lit) { sg.addColorStop(0, '#2b2f16'); sg.addColorStop(1, '#171a0b'); } else { sg.addColorStop(0, '#8a9a45'); sg.addColorStop(1, '#4f5a22'); }
  ctx.fillStyle = sg; ctx.fill(); ctx.lineWidth = 4; ctx.strokeStyle = lit ? '#0e0f06' : '#39410f'; ctx.stroke();
}

/** The painted pumpkin with no square around it: the keyed copy, or (unreadable image) clipped to the lobes + stem box. */
function drawPaintedBody(ctx, lit) {
  if (keyed) { ctx.drawImage(lit ? keyedLit() : keyed.day, 0, 0, CANVAS, CANVAS); return; }
  ctx.save();
  bodyPath(ctx);
  stemBox(ctx);
  ctx.clip();
  ctx.drawImage(painting, 0, 0, CANVAS, CANVAS);
  if (lit) { ctx.fillStyle = DARK_SKIN; ctx.fillRect(0, 0, CANVAS, CANVAS); }
  ctx.restore();
}

/**
 * Draw one pumpkin into a square canvas. `model` = { cuts }. opts: { size (css px), lit, flick (0..1, the candle),
 * bright (Oct 31: every one lit, brighter), backdrop (the carving table), progress (0..1: the replay) }.
 */
export function drawPumpkin(canvas, model, opts = {}) {
  if (!canvas || typeof canvas.getContext !== 'function') return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
  const size = opts.size || canvas.clientWidth || CANVAS;
  const px = Math.max(1, Math.round(size * dpr));
  if (canvas.width !== px) { canvas.width = px; canvas.height = px; }
  ctx.setTransform(px / CANVAS, 0, 0, px / CANVAS, 0, 0);
  ctx.clearRect(0, 0, CANVAS, CANVAS);
  const lit = !!opts.lit;
  const flick = opts.flick == null ? 1 : opts.flick;
  const bright = !!opts.bright;

  if (opts.backdrop) {
    const bg = ctx.createLinearGradient(0, 0, 0, CANVAS);
    if (lit) { bg.addColorStop(0, '#0d0a18'); bg.addColorStop(1, '#170f12'); } else { bg.addColorStop(0, '#3b2a1e'); bg.addColorStop(1, '#23170f'); }
    ctx.fillStyle = bg; ctx.fillRect(0, 0, CANVAS, CANVAS);
    ctx.fillStyle = lit ? 'rgba(255,170,60,0.05)' : 'rgba(0,0,0,0.18)';
    ctx.fillRect(0, 548, CANVAS, 52); // the table edge
  }
  if (lit) { // warm light pooled under a lit pumpkin
    const pool = ctx.createRadialGradient(300, 560, 10, 300, 560, 260);
    pool.addColorStop(0, `rgba(255,170,60,${(bright ? 0.4 : 0.28) * flick})`);
    pool.addColorStop(1, 'rgba(255,170,60,0)');
    ctx.fillStyle = pool; ctx.fillRect(0, 300, CANVAS, 300);
  }

  if (paintingState === 'ready' && painting) drawPaintedBody(ctx, lit); else drawCodeBody(ctx, lit);

  // the cuts, clipped to the body, in order (the replay draws a fraction of them)
  const cuts = (model && Array.isArray(model.cuts)) ? model.cuts : [];
  const show = opts.progress == null ? cuts.length : Math.max(0, Math.min(1, opts.progress)) * cuts.length;
  ctx.save(); bodyPath(ctx); ctx.clip();
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  const rim = lit ? '#ff9a2e' : '#f6c877';
  let hole;
  if (lit) {
    hole = ctx.createRadialGradient(300, 330, 20, 300, 330, 260);
    hole.addColorStop(0, '#fff8cf'); hole.addColorStop(0.45, '#ffd45a'); hole.addColorStop(1, '#ff9a1f');
  } else { hole = '#2e1608'; }
  for (let pass = 0; pass < 2; pass++) {
    if (lit && pass === 1) { ctx.shadowColor = `rgba(255,176,0,${0.9 * flick})`; ctx.shadowBlur = (bright ? 34 : 26) * flick; }
    for (let i = 0; i < Math.ceil(show); i++) {
      const c = cuts[i];
      if (!c || !Array.isArray(c.p) || !c.p.length) continue;
      const frac = Math.min(1, show - i);
      const pts = frac < 1 ? c.p.slice(0, Math.max(2, Math.round(c.p.length * frac))) : c.p;
      const shape = c.k === 'shape' && frac >= 1;
      const w = c.w || 9;
      tracePath(ctx, pts, shape);
      if (pass === 0) {
        if (shape) { ctx.fillStyle = rim; ctx.fill(); ctx.lineWidth = 9; } else { ctx.lineWidth = w + 9; }
        ctx.strokeStyle = rim; ctx.stroke();
      } else if (shape) { ctx.fillStyle = hole; ctx.fill(); } else { ctx.lineWidth = w; ctx.strokeStyle = hole; ctx.stroke(); }
    }
    ctx.shadowBlur = 0; ctx.shadowColor = 'transparent';
  }
  ctx.restore();
}

/** A candle's flicker for sprite `i` at time `t` (the mockup's), steady under reduced motion. */
export function flickAt(t, i = 0, reduce = false) {
  return reduce ? 1 : 0.84 + 0.16 * Math.sin(t / 110 + i * 1.7) * Math.sin(t / 43 + i);
}
